/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown, User, Settings } from "lucide-react";
import { useProfilePic } from "../hooks/useProfilePic";
import { useTenant } from "../context/TenantContext";
import { clearAuthData, normalizeRole, getPublicDisplayName } from "../utils/authUtils";
import { useActiveLearner } from "../hooks/useActiveLearner";
import { syncActiveLearner } from "../utils/studentSession";
import "../styles/Navbar.css";

interface NavbarProps {
  className?: string;
  role?: "superadmin" | "admin" | "headteacher" | "teacher" | "student";
}

const Navbar: React.FC<NavbarProps> = ({ className, role: propRole }) => {
  const navigate = useNavigate();
  const { branding, hasModule } = useTenant();
  const role = normalizeRole(propRole || localStorage.getItem("role"));
  const userName = localStorage.getItem("userName") || "User";
  const userTitle = localStorage.getItem("userTitle") || "";
  const displayName = getPublicDisplayName(userName, userTitle, role);
  const profilePic = useProfilePic(); // Hook handles logic
  const { learners, activeLearner, activeLearnerId } = useActiveLearner();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const schoolLogo = branding.logoUrl;
  const schoolName = branding.displayName;

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = () => {
    clearAuthData();
    localStorage.removeItem("subscriptionBlocked");
    localStorage.removeItem("subscriptionBlockedReason");
    localStorage.removeItem("subscriptionBlockedMessage");
    navigate("/signin");
    setDropdownOpen(false);
  };

  const canUse = (moduleKey?: string) => role === "superadmin" || !moduleKey || hasModule(moduleKey);

  return (
    <header className={`navbar ${role === "superadmin" ? "full-width" : ""} ${className || ""}`}>
      {role !== "superadmin" && (
        <div className="navbar-logo">
          {schoolLogo ? (
            <img
              src={schoolLogo}
              alt={`${schoolName} logo`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              className="navbar-logo-fallback"
              style={{ backgroundColor: branding.primarySoft, color: branding.primaryColor }}
            >
              {branding.initials}
            </div>
          )}
          <h2>{schoolName}</h2>
        </div>
      )}

      <nav className="navbar-nav">
        {role === "admin" && (
          <>
            <Link to="/admindashboard" className="nav-link desktop-only">Dashboard</Link>
            {canUse("teachers") && <Link to="/teachers" className="nav-link desktop-only">Teachers</Link>}
            {canUse("classes") && <Link to="/allclasses" className="nav-link desktop-only">Classes</Link>}
          </>
        )}

        {role === "teacher" && (
          <>
            <Link to="/teacherdashboard" className="nav-link desktop-only">Dashboard</Link>
            {canUse("classes") && <Link to="/classes" className="nav-link desktop-only">My Classes</Link>}
            {canUse("timetable") && <Link to="/timetable" className="nav-link desktop-only">Timetable</Link>}
          </>
        )}

        {role === "student" && learners.length > 0 && (
          <div className="learner-switcher">
            <span className="learner-switcher-label">Learner</span>
            <select
              className="learner-switcher-select"
              value={activeLearnerId || ""}
              onChange={(event) => syncActiveLearner(Number(event.target.value))}
            >
              {learners.map((learner) => (
                <option key={learner.id} value={learner.id}>
                  {learner.name}{learner.className ? ` • ${learner.className}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="relative" ref={dropdownRef}>
          <div
            className="profile-trigger"
            onClick={toggleDropdown}
          >
            <span className="user-name">{displayName}</span>
            <img
              src={profilePic || "/default-avatar.png"}
              alt="Profile"
              className="profile-avatar"
            />
            <ChevronDown
              size={16}
              className={`chevron-icon ${dropdownOpen ? "active" : ""}`}
            />
          </div>

          {dropdownOpen && (
            <div className="navbar-dropdown">
              <div className="dropdown-header">
                <p className="name">{displayName}</p>
                <p className="role">{role || "Student"}</p>
                {role === "student" && activeLearner && (
                  <p className="role">Active learner: {activeLearner.name}</p>
                )}
              </div>

              <div className="dropdown-menu">
                <Link
                  to={
                    role === "superadmin"
                      ? "/superadmin"
                      : role === "admin"
                        ? "/admindashboard"
                        : role === "headteacher"
                          ? "/headteacher"
                          : role === "teacher"
                            ? "/teacherdashboard"
                            : "/dashboard"
                  }
                  className="dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                >
                  <User size={18} />
                  <span>Dashboard</span>
                </Link>

                <Link
                  to="/settings/profile"
                  className="dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                >
                  <Settings size={18} />
                  <span>Profile Settings</span>
                </Link>

                <button
                  onClick={handleLogout}
                  className="dropdown-item logout"
                >
                  <LogOut size={18} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
